import React, { useState, useMemo } from 'react';
import { cn } from '@/shared/lib/utils';

type CalendarView = 'day' | 'month' | 'year';

interface CalendarPickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  'data-testid'?: string;
}

// ─── NavButton must live OUTSIDE the CalendarPickerBase function so that its
// component identity is stable across renders. If it were defined inside, React
// would see a *new* component type on every render and unmount/remount the
// button DOM nodes. That causes the mousedown target to be detached from the
// portal by the time DatePickerWithCalendar's handleClickOutside runs its
// `contains()` check, making the calendar close immediately when any nav arrow
// is clicked.
interface NavButtonProps {
  direction: 'left' | 'right';
  onClick: () => void;
  ariaLabel: string;
  testId: string;
  disabled?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({ direction, onClick, ariaLabel, testId, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'flex items-center justify-center w-7 h-7 rounded-md',
      'hover:bg-surface-alt transition-colors',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    )}
    aria-label={ariaLabel}
    data-testid={testId}
  >
    <svg className="w-4 h-4 text-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  </button>
);

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Number of years shown per page in the year-picker view. */
const YEAR_PAGE_SIZE = 12;

const CalendarPickerBase: React.FC<CalendarPickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(value || new Date());
  const [view, setView] = useState<CalendarView>('day');

  // Year-grid page offset (multiples of YEAR_PAGE_SIZE relative to currentMonth's year)
  const [yearPageOffset, setYearPageOffset] = useState<number>(0);

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // ─── Derived display strings ────────────────────────────────────────────────

  const monthYear = useMemo(() => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(currentMonth);
  }, [currentMonth]);

  const currentYear = currentMonth.getFullYear();

  // First year shown on the current year-page
  const yearPageStart = useMemo(
    () => currentYear + yearPageOffset,
    [currentYear, yearPageOffset],
  );

  const yearGrid = useMemo(
    () => Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => yearPageStart + i),
    [yearPageStart],
  );

  // ─── Day-view helpers ───────────────────────────────────────────────────────

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: Date[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentMonth]);

  const isDateDisabled = (date: Date): boolean => {
    if (disabled) return true;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isCurrentMonth = (date: Date): boolean =>
    date.getMonth() === currentMonth.getMonth();

  const isSelected = (date: Date): boolean => {
    if (!value) return false;
    return (
      date.getDate() === value.getDate() &&
      date.getMonth() === value.getMonth() &&
      date.getFullYear() === value.getFullYear()
    );
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // ─── Month-view helpers ─────────────────────────────────────────────────────

  const isMonthDisabled = (monthIdx: number): boolean => {
    if (disabled) return true;
    const year = currentMonth.getFullYear();
    const lastDayOfMonth = new Date(year, monthIdx + 1, 0);
    const firstDayOfMonth = new Date(year, monthIdx, 1);
    if (minDate && lastDayOfMonth < minDate) return true;
    if (maxDate && firstDayOfMonth > maxDate) return true;
    return false;
  };

  const isSelectedMonth = (monthIdx: number): boolean => {
    if (!value) return false;
    return value.getMonth() === monthIdx && value.getFullYear() === currentMonth.getFullYear();
  };

  const isCurrentMonthHighlight = (monthIdx: number): boolean => {
    const today = new Date();
    return monthIdx === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
  };

  // ─── Year-view helpers ──────────────────────────────────────────────────────

  const isYearDisabled = (year: number): boolean => {
    if (disabled) return true;
    const lastDayOfYear = new Date(year, 11, 31);
    const firstDayOfYear = new Date(year, 0, 1);
    if (minDate && lastDayOfYear < minDate) return true;
    if (maxDate && firstDayOfYear > maxDate) return true;
    return false;
  };

  const isSelectedYear = (year: number): boolean => {
    if (!value) return false;
    return value.getFullYear() === year;
  };

  const isCurrentYear = (year: number): boolean => new Date().getFullYear() === year;

  // ─── Navigation handlers ────────────────────────────────────────────────────

  const handlePreviousMonth = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const handleNextMonth = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  const handlePreviousYear = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    });
  };

  const handleNextYear = (): void => {
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(d.getFullYear() + 1);
      return d;
    });
  };

  const handleDateClick = (date: Date): void => {
    if (!isDateDisabled(date)) {
      onChange(date);
    }
  };

  const handleMonthSelect = (monthIdx: number): void => {
    if (isMonthDisabled(monthIdx)) return;
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(monthIdx);
      return d;
    });
    setView('day');
  };

  const handleYearSelect = (year: number): void => {
    if (isYearDisabled(year)) return;
    setCurrentMonth((prev) => {
      const d = new Date(prev);
      d.setFullYear(year);
      return d;
    });
    setYearPageOffset(0);
    setView('month');
  };

  const handleHeaderClick = (): void => {
    if (disabled) return;
    if (view === 'day') {
      setView('month');
    } else if (view === 'month') {
      setYearPageOffset(0);
      setView('year');
    }
    // year view header click does nothing (already at deepest)
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="bg-surface rounded-lg shadow-lg p-3 w-[280px]"
      data-testid={dataTestId || 'calendar-picker'}
    >
      {/* ── DAY VIEW ─────────────────────────────────────────────────────── */}
      {view === 'day' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={handlePreviousMonth}
              ariaLabel="Previous month"
              testId="calendar-picker-prev-month"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleHeaderClick}
              disabled={disabled}
              className={cn(
                'text-sm font-semibold text-text px-2 py-1 rounded-md',
                'hover:bg-surface-alt transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              aria-label="Select month and year"
              data-testid="calendar-picker-month-year"
            >
              {monthYear}
            </button>
            <NavButton
              direction="right"
              onClick={handleNextMonth}
              ariaLabel="Next month"
              testId="calendar-picker-next-month"
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-text-muted h-7 flex items-center justify-center"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const dateDisabled = isDateDisabled(date);
              const selected = isSelected(date);
              const inCurrentMonth = isCurrentMonth(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateClick(date)}
                  disabled={dateDisabled}
                  className={cn(
                    'h-8 w-full rounded-md text-xs font-normal transition-colors',
                    'flex items-center justify-center',
                    'hover:bg-surface-alt',
                    inCurrentMonth && 'text-text',
                    !inCurrentMonth && 'text-text-muted',
                    selected && 'bg-primary/10 text-primary font-medium hover:bg-primary/20',
                    today && !selected && 'border border-primary',
                    dateDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                    'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
                  )}
                  aria-label={date.toDateString()}
                  data-testid={`calendar-picker-date-${date.getDate()}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ── MONTH VIEW ───────────────────────────────────────────────────── */}
      {view === 'month' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={handlePreviousYear}
              ariaLabel="Previous year"
              testId="calendar-picker-prev-year"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={handleHeaderClick}
              disabled={disabled}
              className={cn(
                'text-sm font-semibold text-text px-2 py-1 rounded-md',
                'hover:bg-surface-alt transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              aria-label="Select year"
              data-testid="calendar-picker-year-header"
            >
              {currentYear}
            </button>
            <NavButton
              direction="right"
              onClick={handleNextYear}
              ariaLabel="Next year"
              testId="calendar-picker-next-year"
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((name, idx) => {
              const monthDisabled = isMonthDisabled(idx);
              const selected = isSelectedMonth(idx);
              const current = isCurrentMonthHighlight(idx);

              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleMonthSelect(idx)}
                  disabled={monthDisabled}
                  className={cn(
                    'h-9 rounded-md text-xs font-medium transition-colors',
                    'flex items-center justify-center',
                    'hover:bg-surface-alt',
                    'text-text',
                    selected && 'bg-primary/10 text-primary hover:bg-primary/20',
                    current && !selected && 'border border-primary',
                    monthDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                    'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
                  )}
                  aria-label={name}
                  data-testid={`calendar-picker-month-${idx}`}
                >
                  {name}
                </button>
              );
            })}
          </div>

        </>
      )}

      {/* ── YEAR VIEW ────────────────────────────────────────────────────── */}
      {view === 'year' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <NavButton
              direction="left"
              onClick={() => setYearPageOffset((o) => o - YEAR_PAGE_SIZE)}
              ariaLabel="Previous years"
              testId="calendar-picker-prev-years"
              disabled={disabled}
            />
            <span
              className="text-sm font-semibold text-text"
              data-testid="calendar-picker-year-range"
            >
              {yearPageStart}–{yearPageStart + YEAR_PAGE_SIZE - 1}
            </span>
            <NavButton
              direction="right"
              onClick={() => setYearPageOffset((o) => o + YEAR_PAGE_SIZE)}
              ariaLabel="Next years"
              testId="calendar-picker-next-years"
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            {yearGrid.map((year) => {
              const yearDisabled = isYearDisabled(year);
              const selected = isSelectedYear(year);
              const current = isCurrentYear(year);

              return (
                <button
                  key={year}
                  type="button"
                  onClick={() => handleYearSelect(year)}
                  disabled={yearDisabled}
                  className={cn(
                    'h-9 rounded-md text-xs font-medium transition-colors',
                    'flex items-center justify-center',
                    'hover:bg-surface-alt',
                    'text-text',
                    selected && 'bg-primary/10 text-primary hover:bg-primary/20',
                    current && !selected && 'border border-primary',
                    yearDisabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                    'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
                  )}
                  aria-label={String(year)}
                  data-testid={`calendar-picker-year-${year}`}
                >
                  {year}
                </button>
              );
            })}
          </div>

        </>
      )}
    </div>
  );
};

export const CalendarPicker = React.memo(CalendarPickerBase);